import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Skeleton } from '@/shared/ui/skeleton';
import { Users, MessageSquare, Heart, HelpCircle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/components/AuthContext';

interface NetworkStats {
  questionsAsked: number;
  answersGiven: number;
  helpfulVotes: number;
}

export const NetworkInsights: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [stats, setStats] = useState<NetworkStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;
    fetchData();
  }, [user?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [questionsResult, answersResult, helpfulResult] = await Promise.all([
        supabase
          .from('network_questions')
          .select('id', { count: 'exact', head: true })
          .eq('author_id', user!.id),
        supabase
          .from('network_answers')
          .select('id', { count: 'exact', head: true })
          .eq('author_id', user!.id),
        supabase
          .from('network_answers')
          .select('helpful_count')
          .eq('author_id', user!.id),
      ]);

      const totalHelpful = (helpfulResult.data ?? []).reduce((sum, a) => sum + (a.helpful_count || 0), 0);

      setStats({
        questionsAsked: questionsResult.count ?? 0,
        answersGiven: answersResult.count ?? 0,
        helpfulVotes: totalHelpful,
      });
    } catch (error) {
      console.error('Failed to fetch network insights:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!stats || (stats.questionsAsked === 0 && stats.answersGiven === 0)) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">{t('analytics.network.noData', 'No Network Activity Yet')}</h3>
          <p className="text-sm text-muted-foreground">{t('analytics.network.noDataDesc', 'Ask questions or help other farmers to see your network insights.')}</p>
        </CardContent>
      </Card>
    );
  }

  const engagementData = [
    { name: t('analytics.network.questions', 'Questions'), value: stats.questionsAsked },
    { name: t('analytics.network.answers', 'Answers'), value: stats.answersGiven },
    { name: 'Helpful Votes', value: stats.helpfulVotes },
  ];

  const totalEngagement = stats.questionsAsked + stats.answersGiven + stats.helpfulVotes;

  return (
    <div className="space-y-6">
      {/* Network Overview */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <HelpCircle className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{t('analytics.network.questions', 'Questions')}</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats.questionsAsked}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{t('analytics.network.answers', 'Answers')}</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats.answersGiven}</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-2">
              <Heart className="h-5 w-5 text-primary" />
              <span className="text-xs text-muted-foreground">{t('analytics.network.helpful', 'Helpful')}</span>
            </div>
            <div className="text-2xl font-bold text-foreground">{stats.helpfulVotes}</div>
          </CardContent>
        </Card>
      </div>

      {/* Engagement Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{t('analytics.network.engagementBreakdown', 'Engagement Breakdown')}</CardTitle>
          <CardDescription>{t('analytics.network.participation', 'Your network participation')}</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--popover))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
              <Bar dataKey="value" fill="hsl(var(--primary))" radius={4} />
            </BarChart>
          </ResponsiveContainer>

          <div className="mt-4 p-3 rounded-lg border bg-card text-center">
            <div className="text-xs text-muted-foreground mb-1">{t('analytics.network.totalEngagement', 'Total Engagement')}</div>
            <div className="text-2xl font-bold text-primary">{totalEngagement}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
